import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { AppointmentStatus } from '@prisma/client';
import SideBarModal from '../modal/SideBarModal';
import useWaitlistEntry from '../../hooks/cancellation-waitlist/useWaitlistEntry';
import { createFooterAction } from '../../helpers/global';
import { getServiceLabel } from '../../helpers/services';
import {
  formatWaitlistDays,
  formatWaitlistTimes,
  formatWaitlistWindow,
} from '../../helpers/cancellation-waitlist';
import { WAITLIST_TIER_LABEL } from '../../constants/cancellation-waitlist';
import { ROUTES } from '../../constants/global';
import { Section } from '../../interfaces';
import { CancellationWaitlistEntryDetail } from '../../types/cancellation-waitlist';

const REQUEST_STATUSES: AppointmentStatus[] = [
  'REQUESTED',
  'ACCEPTED',
  'REJECTED',
  'ARCHIVED',
];

interface Props {
  waitlistEntryId: string | null;
  showProvider: boolean;
  onClose: () => void;
  onNotify: (entry: CancellationWaitlistEntryDetail) => void;
  onRemove: (entry: CancellationWaitlistEntryDetail) => void;
}

function WaitlistEntrySideBarModal({
  waitlistEntryId,
  showProvider,
  onClose,
  onNotify,
  onRemove,
}: Props) {
  const navigate = useNavigate();
  const { data: entry, isLoading, isError } = useWaitlistEntry(waitlistEntryId);

  // Nothing to show without the entry — bail out of the sidebar.
  useEffect(() => {
    if (isError) {
      toast.error('Failed to load the waitlist member.');
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);

  const sections: Section[] = entry
    ? [
        {
          name: 'Member',
          rows: [
            {
              label: 'Customer',
              type: 'TEXT',
              value: [entry.customerDisplayName],
            },
            ...(showProvider
              ? [
                  {
                    label: 'Provider',
                    type: 'TEXT' as const,
                    value: [entry.employeeDisplayName],
                  },
                ]
              : []),
            {
              label: 'Tier',
              type: 'TEXT',
              value: [WAITLIST_TIER_LABEL[entry.tier]],
            },
            {
              label: 'Service',
              type: 'TEXT',
              value: [getServiceLabel(entry.serviceCategory, null)],
            },
            ...(entry.tripCity
              ? [
                  {
                    label: 'Trip',
                    type: 'TEXT' as const,
                    value: [entry.tripCity],
                  },
                ]
              : []),
          ],
        },
        {
          name: 'Preferences',
          rows: [
            {
              label: 'Dates',
              type: 'TEXT',
              value: [formatWaitlistWindow(entry)],
            },
            {
              label: 'Days',
              type: 'TEXT',
              value: [formatWaitlistDays(entry)],
            },
            {
              label: 'Times',
              type: 'TEXT',
              value: [formatWaitlistTimes(entry)],
            },
          ],
        },
        {
          name: 'Activity',
          rows: [
            {
              label: 'Joined',
              type: 'TEXT',
              value: [format(new Date(entry.joinedAt), "M/d/yy 'at' h:mm a")],
            },
            {
              label: 'Last notified',
              type: 'TEXT',
              value: [
                entry.lastNotifiedAt
                  ? format(new Date(entry.lastNotifiedAt), "M/d/yy 'at' h:mm a")
                  : 'Never',
              ],
            },
            {
              label: 'Notifications sent',
              type: 'TEXT',
              value: [String(entry.notificationCount)],
            },
          ],
        },
      ]
    : [];

  const isRequest =
    !!entry?.appointmentStatus &&
    REQUEST_STATUSES.includes(entry.appointmentStatus);
  const viewAppointmentAction = entry?.appointmentId
    ? createFooterAction(isRequest ? 'View Request' : 'View Appointment', () =>
        navigate(
          `${ROUTES[isRequest ? 'requests' : 'calendar']}/${
            entry.appointmentId
          }`,
        ),
      )
    : null;

  return (
    <SideBarModal
      name={entry?.customerDisplayName || 'Waitlist Member'}
      modalOpen={!!waitlistEntryId}
      setModalOpen={onClose}
      isLoading={isLoading}
      sections={sections}
      footerActions={
        entry
          ? [
              ...(viewAppointmentAction ? [viewAppointmentAction] : []),
              createFooterAction('Notify', () => onNotify(entry)),
              createFooterAction('Remove', () => onRemove(entry)),
            ]
          : []
      }
    />
  );
}

export default WaitlistEntrySideBarModal;
